import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NonCompliantWidgetComponent } from "../_shared/widgets/non-compliant-widget/non-compliant-widget.component";
import { ExpiringSoonWidgetComponent } from "../_shared/widgets/expiring-soon-widget/expiring-soon-widget.component";
import { CompliantWidgetComponent } from "../_shared/widgets/compliant-widget/compliant-widget.component";
import { InspectionPassRateWidgetComponent } from "../_shared/widgets/inspection-pass-rate-widget/inspection-pass-rate-widget.component";
import { ComplianceLibraryTabType } from '../../_shared/models/tabs.models';
import { CompliancesComponent } from "./components/compliances/compliances.component";
import { ArchiveComponent } from "./components/archive/archive.component";
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Row } from 'read-excel-file';
import { ComplianceFiltersComponent } from '../_shared/components/compliance-filters/compliance-filters.component';
import { ComplianceCreateModalComponent } from './components/modals/compliance-create-modal/compliance-create-modal.component';
import { ComplianceReviewExcelModalComponent } from './components/modals/compliance-review-excel-modal/compliance-review-excel-modal.component';
import { DownloadTemplateModalComponent } from '../../_shared/components/modals/download-template-modal/download-template-modal.component';
import { readExcelFile } from '../../_shared/helpers/excel-upload.helper';
import {
  convertRowToComplianceImport,
  DEFAULT_COMPLIANCE_EXCEL_COLUMN_SETTINGS,
  downloadComplianceExcelTemplateAsync,
  stripTemplateRows
} from './helpers/compliance-upload.helper';
import { ComplianceImport } from './models/compliances.model';
import { ComplianceStateService } from '../_shared/services/compliance-state.service';
import { CompliancesLibraryService } from './services/compliances-library.service';
import { ComplianceOverviewService } from '../compliance-overview/services/compliance-overview.service';
import { AuthService } from '../../../_shared/services';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { Subject } from 'rxjs';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance',
  standalone: true,
  imports: [
    TranslocoDirective,
    ComplianceFiltersComponent,
    NonCompliantWidgetComponent,
    ExpiringSoonWidgetComponent,
    CompliantWidgetComponent,
    InspectionPassRateWidgetComponent,
    CompliancesComponent,
    ArchiveComponent,
    NgbModule
  ],
  templateUrl: './compliance-library.component.html',
  styleUrl: './compliance-library.component.scss'
})
export class ComplianceLibraryComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal);
  private stateService = inject(ComplianceStateService);
  private libraryService = inject(CompliancesLibraryService);
  private overviewService = inject(ComplianceOverviewService);
  private authService = inject(AuthService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  selectedTab: ComplianceLibraryTabType = 'compliances';
  isDownloadingTemplate = false;

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  ngOnInit(): void {
    this.stateService.clearPageFilters({
      accountIds: true,
      assetType: true,
      complianceType: true,
      complianceRegion: true,
      complianceStandard: true,
      searchTerm: true
    });
  }

  onTabChange(tab: ComplianceLibraryTabType): void {
    this.selectedTab = tab;
  }

  isTabActive(tab: ComplianceLibraryTabType): boolean {
    return this.selectedTab === tab;
  }

  onAddCustomCompliance(): void {
    const modalRef = this.modalService.open(ComplianceCreateModalComponent, {
      centered: true,
      backdrop: 'static',
      size: 'xl'
    });
    modalRef.closed.subscribe(() => this.stateService.refresh());
  }

  async onExcelFileSelectedAsync(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';
    if (!file) {
      return;
    }

    let rows: Row[];
    try {
      rows = await readExcelFile(file);
    } catch {
      this.toastr.error(this.transloco.translate('compliance.library.toasts.couldNotReadFile'));
      return;
    }

    const imports = stripTemplateRows(convertRowToComplianceImport(
      rows, this.authService.getUser().login, new Date(), DEFAULT_COMPLIANCE_EXCEL_COLUMN_SETTINGS));
    if (imports.length === 0) {
      this.toastr.error(this.transloco.translate('compliance.library.toasts.noComplianceRows'));
      return;
    }

    this.openReviewModal(imports);
  }

  onDownloadTemplateClicked(): void {
    const modalRef = this.modalService.open(DownloadTemplateModalComponent, {
      centered: true,
      windowClass: 'download-template-modal-window'
    });
    const modal = modalRef.componentInstance as DownloadTemplateModalComponent;
    modal.title = this.transloco.translate('compliance.library.downloadTemplate.title');
    modal.subtitle = this.transloco.translate('compliance.library.downloadTemplate.subtitle');
    modal.imageSrc = 'assets/images/compliance_excel_template.png';
    modal.imageAlt = this.transloco.translate('assetShared.downloadTemplate.imageAlt');
    modalRef.closed.subscribe(result => {
      if (result === 'download') {
        void this.onDownloadTemplateAsync();
      }
    });
  }

  async onDownloadTemplateAsync(): Promise<void> {
    if (this.isDownloadingTemplate) return;
    this.isDownloadingTemplate = true;
    try {
      const [regionsResponse, frequenciesResponse, routineTypesResponse] = await Promise.all([
        this.overviewService.getComplianceRegionsAsync(this.cancellation),
        this.libraryService.getComplianceFrequenciesAsync(this.cancellation),
        this.libraryService.getRoutineTypesAsync(this.cancellation)
      ]);

      await downloadComplianceExcelTemplateAsync({
        regions: (regionsResponse.data ?? []).map(region => region.name),
        frequencies: (frequenciesResponse.data ?? []).map(frequency => frequency.name),
        routineTypes: (routineTypesResponse.data ?? []).map(routineType => routineType.name)
      });
      this.isDownloadingTemplate = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isDownloadingTemplate = false;
        }
      });
    }
  }

  private openReviewModal(imports: ComplianceImport[]): void {
    const modalRef = this.modalService.open(ComplianceReviewExcelModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'excel-review-modal-window'
    });
    modalRef.componentInstance.imports = imports;
    modalRef.closed.subscribe(() => this.stateService.refresh());
  }
}
